import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import ProductPrice from '../_utils/product-price';
import getOrientation from './get-orientation';
import UrlBuilder from './url-building';
import Favouritable from './favouritable';
import wasPriceType from './types/was-price-type';
import NewProductThumbnail from './new-product-thumbnail';
import NewProductImageThumbnail from './new-product-image-thumbnail';
import RemainingThumbnails from './remaining-thumbnails';
import ProductFlags from './product-flags';

const NewProduct = ({
  category_title: categoryTitle,
  design_code: designCode,
  theme_name: themeName,
  price_range: priceRange,
  delivery_option_keys,
  handleModalContent,
  handleOnClick,
  preSelections,
  preview_images,
  price,
  set_size,
  stacked,
  theme_title,
  title,
  url,
  was_price,
  max_age,
  featured,
  sales_total,
  images,
  extraClasses,
  is_card,
  flag,
  roundOption,
  favourites,
  showThumbnailImages,
  variants,
	productsWithMultipleVariants,
  selectedDeliveryOptions,
  loggedIn
}) => {
  const defaultVariant = (variants || []).find(variant => variant.default);
  const defaultVariantImages = defaultVariant && defaultVariant.images;
  const [isClient, setIsClient] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(defaultVariant);
  const [deliveryOptions, setDeliveryOptions] = useState(selectedDeliveryOptions)
  const [productImages, setProductImages] = useState(
    defaultVariantImages || images
  );

  const desktop = 1280;
  const tablet = 601;
  const calculateQuantity = () => {
    let _quantity = 5,
      _className = 'desktop';
    const windowSize = window.innerWidth;
    if (windowSize < tablet) {
      _quantity = 3;
      _className = 'mobile';
    }
    if (windowSize < desktop && windowSize > tablet) {
      _quantity = 4;
      _className = 'tablet';
    }
    if (windowSize >= desktop) {
      _quantity = 5;
      _className = 'desktop';
    }
    return { quantity: _quantity, viewName: _className };
  };

  let { quantity, viewName } = calculateQuantity();

  const [values, setValues] = useState({
    index: 0,
    isLoading: false,
    showQuantity: quantity,
    viewClass: viewName,
  });
  const handleClickAndSetContent = e => {
    const orientation = getOrientation({ title });
    handleOnClick(e);
    handleModalContent({
      themeTitle: theme_title,
      url: getUrl(),
      priceInfo: {
        price: price,
        price_range: priceRange,
        set_size: set_size,
        was_price: was_price,
      },
      previewImages: preview_images,
      deliveryOptionKeys: delivery_option_keys,
      orientation,
    });
  };

  React.useEffect(() => {
    setIsClient(true);
    const handleWindowResize = () => {
      const { quantity, viewName } = calculateQuantity();
      setValues({ ...values, showQuantity: quantity, viewClass: viewName });
    };
    window.addEventListener('resize', handleWindowResize);
    return () => window.removeEventListener('resize', handleWindowResize);
  }, []);

  React.useEffect(() => {
    setDeliveryOptions(selectedDeliveryOptions);
  }, [selectedDeliveryOptions]);

  const updateImage = newIndex => {
    setValues({ ...values, isLoading: true, index: newIndex });
  };

  const $favoritable = (
    <Favouritable
      designCode={designCode}
      favourited={favourites && favourites.includes(designCode)}
      loggedIn={loggedIn}
    />
  );
  function handleColourSwatchClick(variantName) {
    const variantClicked = variants.find(
      (variant) => variant.name === variantName,
    );
    setSelectedVariant(variantClicked);
    setProductImages(variantClicked.images);
  }
  const getUrl = () => {
    const params = {};
    // for zero down time: add hash as product param
    // urls were of the form category/XXX#{product.to_param}
    // maybe removed later - kamal Need to discuss seem not needed as we have no # in urls
    (() => {
      const hashIndex = url.indexOf('#');
      if (hashIndex > -1) {
        params.product = url.substr(hashIndex + 1);
        url = url.substr(0, hashIndex);
      }
    })();

    // pass on any pre-existing query params
    (() => {
      const queryIndex = url.indexOf('?');
      if (queryIndex > -1) {
        for (const [key, value] of url
          .substr(queryIndex + 1)
          .split('&')
          .map(pair => pair.split('='))) {
          params[key] = value;
        }
        url = url.substr(0, queryIndex);
      }
    })();

    params.theme = themeName;
    params.design_code = designCode;

    if (selectedVariant) {
      params.variant_name = selectedVariant.name;
    }

    if (deliveryOptions !== ''){
      params.selected_delivery_options = deliveryOptions;
    }

    for (const selection in preSelections) {
      if (
        preSelections.hasOwnProperty(selection) &&
        preSelections[selection].length === 1
      ) {
        params[selection] = preSelections[selection][0];
      }
    }

    if (stacked) {
      delete params.product;
    }
    return `${url}?${UrlBuilder.buildURLQuery(params)}`;
  };

  function renderTitle() {
    const Title = () => {
      if (stacked) {
        return <span className="product-category-title">{categoryTitle}</span>;
      }

      return title;
    };

    return (
      <p className="new-product-title-wrapper">
        <strong
          className="theme-title"
          data-max-age={max_age}
          data-featured={featured || '0'}
          data-sales-total={sales_total}>
          {theme_title}
        </strong>
        <span className="product-title">
          <br />
          {Title()}
        </span>
      </p>
    );
  }

  const $thumbnails = productImages.slice(0, values.showQuantity).map((image, i) => {
    return <NewProductImageThumbnail
      thumbnail={image}
      alt={``}
      index={i}
      selected={values.index === i}
      onClickAction={updateImage}
      key={image}
      isLoading={true}
      viewClass={values.viewClass}
    />
  })

  const isRoundedCorners = () => roundOption;
  const isScallopedEdges = () => is_card && /Scalloped/i.test(title);
  const isElegantCorners = () => is_card && /Elegant/i.test(title);
  const isSquareEdges = () => is_card && /Square/i.test(title);
  const isFoldedCard = () => {
    return [/Folded Card/i, /Note Card/i].some(regex => regex.test(title));
  };

  function renderThumbnail() {
    return (
      <NewProductThumbnail
        thumbnails={productImages}
        isCard={
          !isFoldedCard() &&
          !isRoundedCorners() &&
          !isScallopedEdges() &&
          !isElegantCorners() &&
          !isSquareEdges() &&
          is_card
        }
        imageIndex={values.index}
        isLoading={values.isLoading}
        key={productImages}
        roundedCorners={isRoundedCorners()}
        foldedCard={isFoldedCard()}
        scallopedEdges={isScallopedEdges()}
        elegantCorners={isElegantCorners()}
        squareEdges={isSquareEdges()}
        altTag={`${title} with ${theme_title} design`}>
        <ProductFlags flag={flag} was_price={was_price} />
      </NewProductThumbnail>
    );
  }

  const multiplePreviews = (productImages || []).length > 2;
  return (
    <div className={classNames('col')}>
      <div
        data-test-id="new-product"
        className={classNames(
          'product-card new-product d-flex flex-column',
          { stacked: stacked },
          extraClasses
        )}>
        {$favoritable}
        <div className='row m-0 h-100' onClick={handleClickAndSetContent}>
          <div className={classNames('align-content-center col-12 p-0 top-link')}>
            <a href={getUrl()} className='align-content-start d-flex'>{renderThumbnail()}</a>
            <div
              className={classNames(
                'dominant-colours flex-row justify-content-center',
                productsWithMultipleVariants ? 'd-flex' : 'd-none') }>
              {
                variants && variants.length > 1 && variants.map((variant, i) => {
                  if (variant.dominant_colours && variant.dominant_colours.length > 0) {
                    return (
                      <div
                        key={i}
                        className={classNames(
                          'dominant-colour d-flex m-1', {selected: variant.name === selectedVariant.name} 
                        )}
                        data-test-id={variant.name === selectedVariant.name ? 'selected' : 'unselected' }>
                        <button
                          className='dominant-colour-button align-self-center p-0'
                          style={{background: variant.dominant_colours[0]}}
                          onClick={() => handleColourSwatchClick(variant.name)}
                          type='button'
                        />
                      </div>
                    );
                  }
                })
              }
            </div>
            <div className={classNames("new-thumbnails", {'mt-2 align-content-center': showThumbnailImages && multiplePreviews}, {'d-none': !showThumbnailImages})}>
              <div className="thumbnails-wrapper">
                <ul className="list-inline thumbnails d-flex flex-wrap justify-content-center m-0">
                  {showThumbnailImages &&
                    multiplePreviews &&
                    $thumbnails}
                  {showThumbnailImages &&
                    (productImages || []).length > values.showQuantity && (
                      <RemainingThumbnails
                        quantity={productImages.length - values.showQuantity}
                        viewClass={values.viewClass}
                      />
                    )}
                </ul>
              </div>
            </div>
          </div>

          <a href={getUrl()} className='col-12 p-0 align-content-end bottom-link'>
            <div className="title-content">
              <div className="d-flex justify-content-between">
                <ProductPrice
                  price={price}
                  price_range={priceRange}
                  set_size={set_size}
                  was_price={was_price}
                />
              </div>
              {renderTitle()}
            </div>
          </a>
        </div>
      </div>
    </div>
  );
};

NewProduct.propTypes = {
  handleOnClick: PropTypes.func,
  handleModalContent: PropTypes.func,
  image_previews: PropTypes.arrayOf(PropTypes.string),
  price: PropTypes.number,
  price_range: PropTypes.arrayOf(PropTypes.number),
  set_size: PropTypes.number,
  was_price: wasPriceType,
  delivery_option_keys: PropTypes.arrayOf(PropTypes.number),
  theme_title: PropTypes.string,
};

NewProduct.defaultProps = {
  handleOnClick: () => {},
  handleModalContent: () => {},
  image_previews: null,
  delivery_option_keys: null,
  price: null,
  price_range: null,
  set_size: null,
  was_price: null,
  theme_title: null,
};

export default NewProduct;
